import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate, useParams, useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth, startGoogleLogin } from "../../context/AuthContext";
import { BagLogo, BagLogoTile } from "../../components/Logo";
import { PonesiCoinIcon } from "../../components/PonesiCoinIcon";
import { MenuManager } from "../admin/AdminPanel";
import { OptionsManager } from "../admin/OptionsManager";
import { ShoppingBag, UtensilsCrossed, SlidersHorizontal, Settings, Users, ToggleRight, History, ChevronsLeft, ChevronsRight, Menu, X, LogOut, PlusCircle, Sparkles, BarChart3, Clock, Lock } from "lucide-react";

function Loader() {
  return <div className="min-h-screen flex items-center justify-center bg-white"><div className="animate-pop"><BagLogoTile size={96} /></div></div>;
}

function AccessDenied() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center p-6" data-testid="restaurant-access-denied">
      <BagLogo size={56} />
      <h1 className="font-display font-black text-2xl">Nemate pristup</h1>
      <p className="text-slate-500">Nemate dodeljen pristup ovom restoranu.</p>
      <a href="/restaurant" className="text-brand-red font-semibold underline">Nazad</a>
    </div>
  );
}

export function RestaurantLogin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (!loading && user) navigate("/restaurant", { replace: true }); }, [user, loading]);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 p-6 bg-[#F3F4F6]">
      <BagLogo size={64} />
      <div className="text-center">
        <h1 className="font-display font-black text-3xl">Ponesi <span className="text-brand-red">za restorane</span></h1>
        <p className="text-slate-500 mt-2">Prijava za osoblje, menadžere i vlasnike restorana.</p>
      </div>
      <button data-testid="restaurant-google-login" onClick={() => startGoogleLogin("/restaurant")}
        className="bg-brand-red hover:bg-brand-redHover text-white rounded-full px-8 py-3.5 font-display font-bold shadow-lg active:scale-[0.98] transition-transform">
        Prijava putem Google naloga
      </button>
      <a href="/" className="text-sm text-slate-400 underline">Nazad na Ponesi</a>
    </div>
  );
}

export function RestaurantEntry() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [rests, setRests] = useState(null);
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/restaurant/login", { replace: true }); return; }
    api.get("/my/restaurants").then(({ data }) => {
      if (data.length === 1) navigate(`/restaurant/${data[0].id}/orders`, { replace: true });
      else setRests(data);
    }).catch(() => setRests([]));
  }, [user, loading]);
  if (loading || rests === null) return <Loader />;
  if (rests.length === 0) return <AccessDenied />;
  return (
    <div className="min-h-screen bg-[#F3F4F6] p-6">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 mb-6"><BagLogo size={40} /><span className="font-display font-black text-xl">Izaberite restoran</span></div>
        <div className="space-y-3" data-testid="restaurant-selector">
          {rests.map((r) => (
            <button key={r.id} data-testid={`select-rest-${r.id}`} onClick={() => navigate(`/restaurant/${r.id}/orders`)}
              className="w-full text-left bg-white rounded-2xl p-4 border flex items-center justify-between active:scale-[0.99] transition-transform">
              <div><p className="font-display font-black">{r.name}</p><p className="text-xs text-slate-400">{r.category}</p></div>
              <span className="text-[10px] font-bold uppercase bg-slate-100 rounded-full px-2 py-1">{r.my_role}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const NAV = [
  { to: "orders", label: "Porudžbine", icon: ShoppingBag, perm: "orders" },
  { to: "new-order", label: "Vista", icon: PlusCircle, perm: "orders", feature: "VISTA" },
  { to: "availability", label: "Dostupnost", icon: ToggleRight, perm: "any" },
  { to: "history", label: "Istorija", icon: History, perm: "manage" },
  { to: "shifts", label: "Smene", icon: Clock, perm: "manage" },
  { to: "menu", label: "Meni", icon: UtensilsCrossed, perm: "manage" },
  { to: "happy-hour", label: "Happy Hour", icon: Sparkles, perm: "manage" },
  { to: "loyalty", label: "Ponesi Coins", icon: PonesiCoinIcon, perm: "manage", feature: "PONESI_COINS" },
  { to: "analytics", label: "Analitika", icon: BarChart3, perm: "manage" },
  { to: "options", label: "Opcije", icon: SlidersHorizontal, perm: "manage" },
  { to: "settings", label: "Restoran", icon: Settings, perm: "manage" },
  { to: "team", label: "Tim", icon: Users, perm: "team" },
];

function SideNavItem({ rid, n, collapsed, onClick, testid, locked }) {
  const Icon = n.icon;
  return (
    <NavLink to={`/restaurant/${rid}/${n.to}`} data-testid={testid} onClick={onClick}
      title={collapsed ? n.label : undefined}
      className={({ isActive }) => `flex items-center rounded-lg font-semibold text-sm transition-colors ${collapsed ? "justify-center py-2.5" : "gap-3 px-3 py-2.5"} ${isActive ? "bg-brand-red text-white" : locked ? "text-slate-400 hover:bg-slate-50" : "text-slate-600 hover:bg-slate-100"}`}>
      <Icon size={18} className={`shrink-0 ${locked ? "opacity-50" : ""}`} />
      {!collapsed && <span className="truncate">{n.label}</span>}
      {!collapsed && locked && <Lock size={13} data-testid={`${testid}-lock`} className="ml-auto shrink-0 text-slate-400" />}
    </NavLink>
  );
}

function SidebarBody({ rid, nav, collapsed, onNavigate, logout, features = {}, testidPrefix = "rnav-" }) {
  const item = (className) => `flex items-center rounded-lg text-sm font-semibold transition-colors ${collapsed ? "justify-center py-2.5" : "gap-2 px-3 py-2"} ${className}`;
  return (
    <>
      <nav className="space-y-1 flex-1 px-2">
        {nav.map((n) => (
          <SideNavItem key={n.to} rid={rid} n={n} collapsed={collapsed} onClick={onNavigate}
            locked={!!n.feature && features[n.feature] === false}
            testid={`${testidPrefix}${n.to}`} />
        ))}
      </nav>
      <div className="px-2 pb-3 pt-2 mt-2 border-t space-y-1">
        <button onClick={logout} data-testid="restaurant-logout" title={collapsed ? "Odjava" : undefined}
          className={item("text-brand-red hover:bg-red-50 w-full")}>
          <LogOut size={16} className="shrink-0" />{!collapsed && <span>Odjava</span>}
        </button>
      </div>
    </>
  );
}

export function RestaurantLayout() {
  const { user, loading, logout } = useAuth();
  const doLogout = async () => { await logout(); window.location.href = "/restaurant/login"; };
  const { rid } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [role, setRole] = useState(null);
  const [denied, setDenied] = useState(false);
  const [features, setFeatures] = useState({});
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("restaurant_sidebar_collapsed") === "true");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("restaurant_sidebar_collapsed", String(!c)); return !c; });

  const load = useCallback(async () => {
    const { data } = await api.get("/my/restaurants");
    const r = data.find((x) => x.id === rid);
    if (!r) { setDenied(true); return; }
    setRestaurant(r); setRole(r.my_role);
    // Paid-service entitlements are ALWAYS read from the backend and never cached across loads,
    // so a PLATFORM_ADMIN change is reflected on the next refresh/navigation.
    try {
      const { data: f } = await api.get(`/restaurant/${rid}/features`);
      setFeatures(f);
    } catch { setFeatures({}); }
  }, [rid]);
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/restaurant/login", { replace: true }); return; }
    load().catch(() => setDenied(true));
  }, [user, loading, rid]);

  // Canonical role/entitlements are re-read whenever the dashboard comes back to the foreground,
  // so a role change (e.g. MANAGER → STAFF) also updates navigation on non-board pages.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") load().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
    // eslint-disable-next-line
  }, [rid]);

  if (loading || (!restaurant && !denied)) return <Loader />;
  if (denied) return <AccessDenied />;

  const R = (role || "").toUpperCase();
  const canManage = ["PLATFORM_ADMIN", "MANAGER", "OWNER"].includes(R);
  const canTeam = ["PLATFORM_ADMIN", "OWNER"].includes(R);
  const allowed = (perm) => perm === "any" || perm === "orders" || (perm === "manage" && canManage) || (perm === "team" && canTeam);
  const nav = NAV.filter((n) => allowed(n.perm));

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex">
      {/* Desktop collapsible sidebar */}
      <aside data-testid="restaurant-sidebar"
        className={`hidden md:flex flex-col bg-white border-r transition-[width] duration-200 ease-in-out ${collapsed ? "w-[68px]" : "w-56"}`}>
        <div className={`flex items-center pt-4 pb-1 ${collapsed ? "justify-center px-0" : "gap-2 px-4"}`}>
          <BagLogo size={collapsed ? 30 : 34} />
          {!collapsed && <span className="font-display font-black text-lg">Ponesi</span>}
        </div>
        {!collapsed && <p className="px-4 text-xs text-slate-400 truncate" data-testid="layout-rest-name">{restaurant.name} · {role}</p>}
        <button data-testid="sidebar-collapse-toggle" onClick={toggleCollapsed} title={collapsed ? "Proširi meni" : "Skupi meni"}
          className={`flex items-center justify-center rounded-lg py-1.5 my-2 text-slate-400 hover:bg-slate-100 ${collapsed ? "mx-2" : "mx-3 self-end px-2"}`}>
          {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
        </button>
        <SidebarBody rid={rid} nav={nav} collapsed={collapsed} logout={doLogout} features={features} />
      </aside>

      {/* Mobile off-canvas drawer */}
      <div className={`md:hidden fixed inset-0 z-50 ${drawerOpen ? "" : "pointer-events-none"}`} data-testid="sidebar-drawer">
        <div onClick={() => setDrawerOpen(false)} data-testid="drawer-overlay"
          className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${drawerOpen ? "opacity-100" : "opacity-0"}`} />
        <aside className={`absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl flex flex-col transition-transform duration-200 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex items-center justify-between px-4 pt-4 pb-1">
            <div className="flex items-center gap-2"><BagLogo size={32} /><span className="font-display font-black text-lg">Ponesi</span></div>
            <button data-testid="drawer-close" onClick={() => setDrawerOpen(false)} className="p-2 rounded-lg hover:bg-slate-100"><X size={20} /></button>
          </div>
          <p className="px-4 text-xs text-slate-400 truncate mb-2">{restaurant.name} · {role}</p>
          <SidebarBody rid={rid} nav={nav} collapsed={false} logout={doLogout} features={features} onNavigate={() => setDrawerOpen(false)} testidPrefix="rnav-m-" />
        </aside>
      </div>

      <div className="flex-1 min-w-0">
        <div className="md:hidden flex items-center gap-3 p-3 bg-white border-b sticky top-0 z-30">
          <button data-testid="sidebar-hamburger" onClick={() => setDrawerOpen(true)} className="p-2 rounded-lg hover:bg-slate-100"><Menu size={22} /></button>
          <div className="flex items-center gap-2 min-w-0"><BagLogo size={26} /><span className="font-display font-black truncate">{restaurant.name}</span></div>
        </div>
        <main className="p-4 md:p-6">
          <Outlet context={{ restaurant, role, features, reload: load }} />
        </main>
      </div>
    </div>
  );
}

// Route guard: management pages never render for STAFF, not even for a frame on a direct URL.
export function ManagerOnly({ perm = "manage", children }) {
  const { restaurant, role } = useOutletContext();
  const R = (role || "").toUpperCase();
  const ok = perm === "team"
    ? ["PLATFORM_ADMIN", "OWNER"].includes(R)
    : ["PLATFORM_ADMIN", "MANAGER", "OWNER"].includes(R);
  if (!ok) return <Navigate to={`/restaurant/${restaurant.id}/orders`} replace />;
  return children;
}

export function MenuPage() {
  const { restaurant } = useOutletContext();
  return <MenuManager restaurants={[restaurant]} lockRestaurant />;
}

export function OptionsPage() {
  const { restaurant } = useOutletContext();
  return <OptionsManager restaurants={[restaurant]} lockRestaurant />;
}
